import { FormSchema } from '../../../types/formSchema';
import {
  createParentSection,
  createLeafSection,
  createParentSectionWithFilters,
} from '../../../utils/documentSectionHelpers';
import {
  ACTUALS_LABEL,
  ACTUALS_VALUE,
  USD_CONSTANT_LABEL,
  USD_CONSTANT_VALUE,
  USD_NOMINAL_LABEL,
  USD_NOMINAL_VALUE,
  PERIOD_TREE_DATA,
  DEFAULT_PERIOD,
  STEP_IDS,
} from '../../../constants/mfr-constants';
import {
  PL_DRILL_DOWN_OPTIONS,
  PL_SLICE_OPTIONS,
} from 'src/config/mfr-workflow-config';
import { isFeatureEnabled } from '../../../config/feature-flags';

export interface MarketplaceNode {
  title: string;
  value: string;
  key: string;
  children?: MarketplaceNode[];
}

// EU5 marketplace nodes
const EU5_MARKETPLACES: MarketplaceNode[] = [
  { title: 'DE - Germany', value: 'DE', key: 'DE' },
  { title: 'ES - Spain', value: 'ES', key: 'ES' },
  { title: 'FR - France', value: 'FR', key: 'FR' },
  { title: 'IT - Italy', value: 'IT', key: 'IT' },
  { title: 'UK - United Kingdom', value: 'UK', key: 'UK' },
];

// Additional EU10 marketplaces (not in EU5)
const EU10_ADDITIONAL_MARKETPLACES: MarketplaceNode[] = [
  { title: 'NL - Netherlands', value: 'NL', key: 'NL' },
  { title: 'PL - Poland', value: 'PL', key: 'PL' },
  { title: 'SE - Sweden', value: 'SE', key: 'SE' },
  { title: 'BE - Belgium', value: 'BE', key: 'BE' },
  { title: 'IE - Ireland', value: 'IE', key: 'IE' },
];

// EU5 group
const EU5_GROUP: MarketplaceNode = {
  title: 'EU5',
  value: 'EU5',
  key: 'EU5',
  children: EU5_MARKETPLACES,
};

// EU10 group (contains EU5 nested)
const EU10_GROUP: MarketplaceNode = {
  title: 'EU10',
  value: 'EU10',
  key: 'EU10',
  children: [EU5_GROUP, ...EU10_ADDITIONAL_MARKETPLACES],
};

// LATAM group
const LATAM_GROUP: MarketplaceNode = {
  title: 'LATAM',
  value: 'LATAM',
  key: 'LATAM',
  children: [
    { title: 'BR - Brazil', value: 'BR', key: 'BR' },
    { title: 'MX - Mexico', value: 'MX', key: 'MX' },
  ],
};

// MENA group
const MENA_GROUP: MarketplaceNode = {
  title: 'MENA',
  value: 'MENA',
  key: 'MENA',
  children: [
    { title: 'AE - United Arab Emirates', value: 'AE', key: 'AE' },
    { title: 'EG - Egypt', value: 'EG', key: 'EG' },
    { title: 'SA - Saudi Arabia', value: 'SA', key: 'SA' },
  ],
};

// NA Established group
const NA_ESTABLISHED_GROUP: MarketplaceNode = {
  title: 'NA Established',
  value: 'NA_ESTABLISHED',
  key: 'NA_ESTABLISHED',
  children: [],
};

// Standalone marketplaces
const STANDALONE_MARKETPLACES: MarketplaceNode[] = [
  { title: 'AU - Australia', value: 'AU', key: 'AU' },
  { title: 'CA - Canada', value: 'CA', key: 'CA' },
  { title: 'IN - India', value: 'IN', key: 'IN' },
  { title: 'JP - Japan', value: 'JP', key: 'JP' },
  { title: 'SG - Singapore', value: 'SG', key: 'SG' },
  { title: 'TR - Turkey', value: 'TR', key: 'TR' },
  { title: 'US - United States', value: 'US', key: 'US' },
];

const getCurrencyOptions = () => {
  const options = [{ value: USD_CONSTANT_VALUE, label: USD_CONSTANT_LABEL }];
  if (isFeatureEnabled('USD_NOMINAL')) {
    options.push({ value: USD_NOMINAL_VALUE, label: USD_NOMINAL_LABEL });
  }
  return options;
};

const getMarketplaceTreeData = (): MarketplaceNode[] => {
  const treeData: MarketplaceNode[] = [];

  // EU10 group with EU5 nested (feature flagged) or legacy EU5 at top level
  treeData.push(isFeatureEnabled('MARKETPLACE_EU10') ? EU10_GROUP : EU5_GROUP);

  // Regional groups (always included)
  treeData.push(LATAM_GROUP);
  treeData.push(MENA_GROUP);

  // NA Established (feature flagged)
  if (isFeatureEnabled('MARKETPLACE_NA_ESTABLISHED')) {
    treeData.push(NA_ESTABLISHED_GROUP);
  }

  // Add EU10 additional marketplaces as standalone when EU10 flag is disabled
  if (!isFeatureEnabled('MARKETPLACE_EU10')) {
    treeData.push(...EU10_ADDITIONAL_MARKETPLACES);
  }

  // Always include standalone marketplaces
  treeData.push(...STANDALONE_MARKETPLACES);

  return treeData;
};

export const getMfrFormSchema = (): FormSchema => ({
  id: 'mfr-create',
  title: 'Create MFR Report',
  description:
    'Configure your MFR report with the required filters and document sections.',
  sections: [
    {
      title: 'Basic Information',
      description:
        'Configure the basic information to start your report generation',
      layout: 'single-column',
      fields: [
        {
          type: 'text',
          name: 'name',
          label: 'Name',
          placeholder: 'Enter name...',
          validation: [{ required: true, message: 'Name is required' }],
          componentProps: {
            allowClear: true,
            showCount: true,
            maxLength: 100,
          },
        },
        {
          type: 'textarea',
          name: 'description',
          label: 'Description',
          placeholder: 'Enter description...',
          componentProps: {
            allowClear: true,
            showCount: true,
            maxLength: 500,
            autoSize: { minRows: 3, maxRows: 6 },
          },
        },
      ],
    },
    {
      title: 'Filters',
      layout: 'two-column',
      fields: [
        {
          type: 'treeSelect',
          name: 'marketplace',
          label: 'Marketplace',
          placeholder: 'Select marketplace...',
          multiple: true,
          maxTagCount: 2,
          treeDefaultExpandAll: false,
          treeData: getMarketplaceTreeData(),
          validation: [{ required: true, message: 'Marketplace is required' }],
        },
        {
          type: 'multiselect',
          name: 'channel',
          label: 'Channel',
          placeholder: 'Select channels...',
          options: [
            { value: 'FBA', label: 'FBA' },
            { value: 'MFN', label: 'MFN' },
            { value: 'Retail', label: 'Retail' },
          ],
          validation: [{ required: true, message: 'Channel is required' }],
        },
        {
          type: 'treeSelect',
          name: 'product_lines',
          label: 'Product Line',
          placeholder: 'Select product line...',
          treeDefaultExpandAll: false,
          showSearch: true,
          allowClear: true,
          loadData: true,
          filterType: 'productid',
          treeData: [
            {
              value: 'A1PTOT',
              title: 'A1PTOT - Total Products Alternate',
              key: 'A1PTOT',
              hierarchyType: 'COGNOS_PRODUCT_ALTERNATE_HIERARCHY',
              isRootNode: true,
              isLeaf: false,
            },
            {
              value: 'PTOT',
              title: 'PTOT - Total Products',
              key: 'PTOT',
              hierarchyType: 'COGNOS_PRODUCT_HIERARCHY',
              isRootNode: true,
              isLeaf: false,
            },
            {
              value: 'altProduct',
              title: 'altProduct',
              key: 'altProduct',
              hierarchyType: 'COGNOS_PRODUCT_ALTERNATE_ALT_HIERARCHY',
              isRootNode: true,
              isLeaf: false,
            },
          ],
          validation: [{ required: true, message: 'Product Line is required' }],
        },
        {
          type: 'treeSelect',
          name: 'period',
          label: 'Period',
          placeholder: 'Select period...',
          treeDefaultExpandAll: false,
          treeData: PERIOD_TREE_DATA,
          defaultValue: DEFAULT_PERIOD,
          validation: [{ required: true, message: 'Period is required' }],
        },
        {
          type: 'dropdown',
          name: 'base_scenario',
          label: 'Base Scenario',
          defaultValue: ACTUALS_VALUE,
          readonly: true,
          options: [{ value: ACTUALS_VALUE, label: ACTUALS_LABEL }],
          validation: [
            { required: true, message: 'Base Scenario is required' },
          ],
        },
        {
          type: 'multiselect',
          name: 'comparison_scenario',
          label: 'Comparison Scenario',
          placeholder: 'Select comparison scenario...',
          maxTagCount: 2,
          options: [],
          validation: [
            { required: true, message: 'Comparison Scenario is required' },
          ],
        },
        {
          type: 'dropdown',
          name: 'currency',
          label: 'Currency',
          defaultValue: USD_CONSTANT_VALUE,
          options: getCurrencyOptions(),
          warningMessage: {
            condition: { value: USD_NOMINAL_VALUE },
            text: "Currency filter doesn't apply to Business Trends. This section is based on transaction data and always reports in USD Constant.",
          },
          validation: [{ required: true, message: 'Currency is required' }],
        },
        {
          type: 'dropdown',
          name: 'data_source',
          label: 'Data Source',
          defaultValue: 'Inc Ads',
          options: [
            { value: 'Inc Ads', label: 'Including Advertising' },
            { value: 'Ex Ads', label: 'Excluding Advertising' },
          ],
          warningMessage: {
            condition: { value: 'Ex Ads' },
            text: "Data Source filter doesn't apply to Business Trends. This section is based on transaction data and always includes Advertising.",
          },
          validation: [{ required: true, message: 'Data Source is required' }],
        },
      ],
    },
    {
      title: 'Document Sections',
      layout: 'single-column',
      fields: [
        {
          type: 'documentSectionsTable',
          name: 'documentSections',
          label: '',
          tableData: [
            // Executive P&L Group
            createParentSection('exec-pl', 'Executive P&L', [
              createLeafSection(
                STEP_IDS.EXECUTIVE_PNL_EXECUTIVE_SUMMARY,
                'Executive Summary',
              ),
              createLeafSection(
                STEP_IDS.EXECUTIVE_PNL_PNL_BY_CHANNEL,
                'P&L by Channel',
              ),
              createLeafSection(STEP_IDS.EXECUTIVE_PNL_PNL_BY_GL, 'P&L by GL', {
                hasFilter: true,
                filterType: 'multiselect',
                filterDependsOn: 'productLine',
                filterPlaceholder: 'Select Product Line',
                filterOptions: [],
                maxCount: 12,
              }),
              createLeafSection(
                STEP_IDS.EXECUTIVE_PNL_PNL_BY_MARKETPLACE,
                'P&L by Marketplace',
              ),
            ]),

            // P&L by Slice Group
            createParentSectionWithFilters('pl-slice', 'P&L by Slice', {
              filterType: 'multiselect',
              filterPlaceholder: 'Add Slice',
              filterOptions: PL_SLICE_OPTIONS,
            }),

            // Business Trends Group
            createParentSection('business-trends', 'Business Trends', [
              createLeafSection(
                STEP_IDS.BUSINESS_TRENDS_TOP_BOTTOM_BRANDS,
                'Top and Bottom 10 Brands',
              ),
              createLeafSection(
                STEP_IDS.BUSINESS_TRENDS_TOP_BOTTOM_ASINS,
                'Top and Bottom 10 ASINs',
              ),
            ]),

            // Cost to Serve (standalone)
            createLeafSection(STEP_IDS.COST_TO_SERVE, 'Cost to Serve'),

            // Detailed P&L (standalone)
            createLeafSection(STEP_IDS.DETAILED_PNL, 'Detailed P&L'),

            // P&L Drill Down Group
            createParentSectionWithFilters('pl-drill-down', 'P&L Drill Down', {
              filterType: 'multiselect',
              filterPlaceholder: 'Add Pnl Line',
              filterOptions: PL_DRILL_DOWN_OPTIONS,
            }),
          ],
        },
      ],
    },
  ],
});

/** @deprecated Use getMfrFormSchema() instead */
export const mfrFormSchema = getMfrFormSchema();
